import { PatientallergySeverityEnum } from "@/services";
import { SectionGroupDef } from "./readmission-f2f-types";

export const CURRENT_VISIT_PLACEHOLDER =
  "Pick an option or type. The note text builds here.";
export const CURRENT_VISIT_PANEL_WIDTH_PX = 360;
// Matches the six-row text fields: the summary scrolls inside the panel past this.
export const CURRENT_VISIT_PANEL_MAX_HEIGHT_PX = 200;
export const SECTION_GROUP_HEADING_FONT_SIZE = 14;
export const SECTION_GROUP_HEADING_COLOR = "primary.main";
export const SECTION_TITLE_FONT_SIZE = 12;
export const SECTION_TITLE_COLOR = "text.secondary";
export const CURRENT_VISIT_TITLE_FONT_WEIGHT = 700;
export const CURRENT_VISIT_TITLE_COLOR = "text.primary";
export const CURRENT_VISIT_TEXT_FONT_WEIGHT = 400;
export const CURRENT_VISIT_TEXT_COLOR = "text.secondary";
export const FREE_TEXT_MIN_ROWS = 3;
// Text fields grow to this many lines, then scroll inside the box.
export const FREE_TEXT_MAX_ROWS = 6;
export const EMPTY_ROW_ENTRY = { checked: false, text: "" } as const;
// Sections whose state lives in the note payload drop their DOM while collapsed.
export const UNMOUNT_ON_COLLAPSE_ACCORDION_PROPS = {
  TransitionProps: { unmountOnExit: true },
} as const;
export const MAX_LIST_ITEMS = 50;
export const EXPAND_ALL_LABEL = "Expand all";
export const COLLAPSE_ALL_LABEL = "Collapse all";
export const OTHERS_OPTION = "Others";
export const DETAIL_CARD_HEIGHT_PX = 90;
export const DETAIL_CARD_GAP_PX = 8;
export const DETAIL_CARD_BACKGROUND = "#1A1A1A0D";
// A long option wraps; the box or radio stays on its first line instead of centring on the block.
export const OPTION_ROW_SX = { alignItems: "flex-start" } as const;
export const OPTION_CONTROL_SX = { py: 0.25 } as const;
export const SOCIAL_SUB_FIELD_MIN_WIDTH_PX = 260;
// Selections this long are statements; the Current Visit panel lists them one per line.
export const SUMMARY_STATEMENT_MIN_WORDS = 8;
export const SENTENCE_END_PATTERN = /[.!?]$/;
export const FREE_TEXT_LABEL = "Additional note";
// Prefix for a group's typed details so the report prints them as their own "Note:" bullet.
export const GROUP_NOTE_PREFIX = "Note: ";
export const FREE_TEXT_PLACEHOLDER = "Specify Details...";
export const CURRENT_VISIT_BULLET = "\u2022 ";
export const NESTED_OPTIONS_REQUIRED = "Select at least one.";
export const NESTED_OPTIONS_CLEAR_CONFIRM = {
  TITLE: "Clear selections?",
  CONTENT: "The ticked options under this choice will be cleared.",
  CONFIRM: "Clear",
} as const;
export const FREE_TEXT_NOTE_LABEL = "Free text";
export const FREE_TEXT_ANSWER_ROWS = 3;
export const CHIEF_COMPLAINT_PLACEHOLDER = "Chief Complaint...";
export const REMOVE_ENTRY_LABEL = "Remove";
export const EDIT_ENTRY_LABEL = "Edit";
export const PHARMACY_LABELS = {
  section: "PREFERRED PHARMACY",
  name: "Pharmacy",
  phone: "Phone",
  fax: "Fax",
  address: "Address",
} as const;
export const PHARMACY_SELECT_PLACEHOLDER = "Select Pharmacy";
export const PHARMACY_PREFERENCE_TYPE = "PHARMACY";
export const PHARMACY_LIST_PAGE_SIZE = 20;
export const ADD_PHARMACY_LABEL = "Add new pharmacy";
export const PHARMACY_RECORD_NAME = "Pharmacy";
export const EDIT_PHARMACY_TITLE = "Edit Pharmacy";
export const EDIT_PHARMACY_SUBMIT_LABEL = "Save Pharmacy";
export const NON_DIGIT_PATTERN = /\D/g;
export const PHARMACY_HINT =
  "Select one or more preferred pharmacies, or add a new one to the directory.";

export const ALLERGY_SEVERITY_PLACEHOLDER = "Select";
export const ALLERGY_SEVERITY_OPTIONS: {
  value: PatientallergySeverityEnum;
  label: string;
}[] = [
  { value: PatientallergySeverityEnum.Mild, label: "Mild" },
  { value: PatientallergySeverityEnum.Moderate, label: "Moderate" },
  { value: PatientallergySeverityEnum.Severe, label: "Severe" },
];
export const ALLERGY_FIELD_LABELS = {
  allergy: "Allergy",
  severity: "Severity",
  notes: "Notes",
} as const;
export const ALLERGY_FIELD_FLEX = { flex: 1, minWidth: 180 } as const;
export const ALLERGY_NAME_PLACEHOLDER = "Allergy name";
export const ALLERGY_NOTES_PLACEHOLDER = "Notes";
export const ALLERGY_ADD_PLACEHOLDER =
  "Type one or several allergies, separated by commas, then press Enter";
export const ALLERGY_ADD_BUTTON_LABEL = "Add allergy";
export const ALLERGY_RECORD_NAME = "Allergy";
export const PATIENT_ALLERGY_LIST_KEY = "patient-allergy-list";
export const ALLERGY_LIST_PAGE_SIZE = 100;

export const HISTORY_LIST_PAGE_SIZE = 100;
export const HISTORY_FIELD_FLEX = { flex: 1, minWidth: 180 } as const;
export const HISTORY_NOTES_LABEL = "Notes";
export const HISTORY_NOTES_PLACEHOLDER = "Notes";
export const HISTORY_FREE_TEXT_LABEL = FREE_TEXT_LABEL;
export const HISTORY_FREE_TEXT_PLACEHOLDER = FREE_TEXT_PLACEHOLDER;

export const PATIENT_MEDICAL_HISTORY_LIST_KEY = "patient-medical-history-list";
export const PATIENT_SURGICAL_HISTORY_LIST_KEY =
  "patient-surgical-history-list";
export const PATIENT_FAMILY_HISTORY_LIST_KEY = "patient-family-history-list";

export const SOCIAL_HISTORY_TYPE = "HOME_CARE_SUBSTANCE_USE" as const;
export const SOCIAL_HISTORY_LIST_KEY = "all-patient-social-history";

export const MEDICAL_HISTORY_RECORD_NAME = "Medical history";
export const MEDICAL_HISTORY_NAME_LABEL = "Condition";
export const MEDICAL_HISTORY_LABELS = {
  namePlaceholder: "Condition",
  addButtonLabel: "Add medical history",
} as const;

export const SURGICAL_HISTORY_RECORD_NAME = "Surgical history";
export const SURGICAL_HISTORY_NAME_LABEL = "Surgery";
export const SURGICAL_HISTORY_LABELS = {
  namePlaceholder: "Surgery",
  addButtonLabel: "Add surgical history",
} as const;

export const FAMILY_HISTORY_RECORD_NAME = "Family history";
export const FAMILY_HISTORY_NAME_LABEL = "Condition";
export const FAMILY_HISTORY_LABELS = {
  namePlaceholder: "Condition",
  addButtonLabel: "Add family history",
} as const;

export const READMISSION_SECTIONS: SectionGroupDef[] = [
  {
    heading: "Subjective",
    sections: [
      { key: "chiefComplaints", title: "Chief Complaint" },
      { key: "hpi", title: "History of Present Illness" },
      { key: "hospitalization", title: "List of Hospitalization" },
      { key: "medicalHistory", title: "Medical History" },
      { key: "surgicalHistory", title: "Surgical History" },
      { key: "familyHistory", title: "Family History" },
      { key: "socialHistory", title: "Social History" },
      { key: "allergy", title: "Allergy" },
      { key: "medication", title: "Medication List & Pharmacy" },
      { key: "lastPcp", title: "Last PCP Visit and Labs" },
      { key: "specialist", title: "Current Medical Specialist" },
    ],
  },
  {
    heading: "Objective",
    sections: [
      { key: "reviewOfSystems", title: "Review of Systems" },
      { key: "vitalSigns", title: "Vital Signs" },
      { key: "physicalExam", title: "Physical Exam" },
      { key: "homebound", title: "Functional & Homebound Status" },
    ],
  },
  {
    heading: "Assessment",
    sections: [
      { key: "diagnosis", title: "Diagnosis" },
      { key: "annualWellness", title: "Annual Wellness (Internal use)" },
      { key: "diabeticScreening", title: "Diabetic Screening" },
      { key: "smokingScreening", title: "Smoking Screening" },
      { key: "obesityScreening", title: "Obesity Screening" },
    ],
  },
  {
    heading: "Plan",
    sections: [
      { key: "planOfCare", title: "Plan of Care" },
      { key: "advanceDirectives", title: "Advance Directives" },
      { key: "patientInstructions", title: "Patient Instructions" },
      { key: "faceToFace", title: "Face to Face Encounter" },
      { key: "homeHealthOrder", title: "Home Health Order" },
      { key: "startOfCare", title: "Start of Care" },
    ],
  },
];

export const HOSPITALIZATION_ROWS = [
  {
    key: "Patient denies",
    placeholder: "e.g. any hospitalization in the past year",
  },
  {
    key: "Patient was hospitalized",
    placeholder: "e.g. at Northridge Hospital last May 2026",
  },
  {
    key: "Reason of hospitalization",
    placeholder: "e.g. fall and extensive pain on the hip",
  },
];

export const HOSPITALIZATION_EXCLUSIVE: Record<string, string[]> = {
  "Patient denies": ["Patient was hospitalized", "Reason of hospitalization"],
};

export const LAST_PCP_ROWS = [
  { key: "Last PCP visit", placeholder: "e.g. 09/2026, normal" },
  { key: "Last mammogram", placeholder: "e.g. 09/2026, normal" },
  { key: "Last endoscopy", placeholder: "e.g. 09/2026, normal" },
  { key: "Last colonoscopy", placeholder: "e.g. 09/2026, normal" },
  { key: "Most recent lab exam", placeholder: "e.g. 09/2026, normal" },
];

export const SPECIALIST_OPTIONS = [
  "Cardiologist",
  "Gastroenterologist",
  "Pulmonologist",
  "Neurologist",
  "Pain Management Specialist",
  "Orthopedist",
  "Wound Care Specialist",
  "Psychiatrist",
  "Endocrinologist",
  "Rheumatologist",
  "Nephrologist",
  "Oncologist",
  "Hematologist",
  "Gynecologist",
  "Podiatrist",
  "ENT Specialist",
  "Dermatologist",
  "Dietitian",
  "Ophthalmologist",
  "Optometrist",
];
export const SPECIALIST_PLACEHOLDER = "e.g. manages patient's meds";

export const READMISSION_SECTION_KEYS: string[] = READMISSION_SECTIONS.flatMap(
  (group) => group.sections.map((section) => section.key)
);
